import { action, computed, makeObservable, observable } from 'mobx'
import type { FirebaseRepository } from '../models/FirebaseRepository'
import { Cubit } from './core'
import { PromptType } from '../firestore/PromptVersion/types'
import {
  InteractiveElementPromptVersion,
  PostAssignmentPromptVersion,
  PostRoomStatePromptVersion,
  RoomStatePromptVersion,
  RubricPromptVersion,
  ToolPromptVersion,
} from '../models/PromptVersion'
import { RubricType, type StaticModelCollection } from '../types'
import { PostRoomStatePromptType } from '../firestore/PromptPostRoomStateType/types'
import { getPromptVersions } from '../firestore/PromptVersion'
import { InteractiveElementPromptType } from '../firestore/PromptInteractiveElementType/types'
import { CurrentPromptVersion } from '../models/CurrentPromptVersion'
import { getCurrentPromptVersions } from '../firestore/CurrentPromptVersion'
import { PromptRubricType } from '../models/PromptRubricType'
import { PromptPostRoomStateType } from '../models/PromptPostRoomStateType'
import { PromptInteractiveElementType } from '../models/PromptInteractiveElementType'
import {
  getPromptPostRoomStateType,
  updatePromptPostRoomStateType,
} from '../firestore/PromptPostRoomStateType'
import {
  getRoomPromptType,
  updatePromptRubricType,
  updatePromptRubricTypeScoreDescription,
} from '../firestore/PromptRubricType'
import {
  getPromptInteractiveElementType,
  updatePromptInteractiveElementType,
} from '../firestore/PromptInteractiveElementType'
import { PromptRoomStateType } from '../models/PromptRoomStateType'
import { PromptToolType } from '../models/PromptToolType'
import {
  getPromptRoomStateType,
  updatePromptRoomStateType,
} from '../firestore/PromptRoomStateType'
import { RoomStatePromptType } from '../firestore/PromptRoomStateType/types'
import { ToolPromptType } from '../firestore/PromptToolType/types'
import {
  getPromptToolType,
  updatePromptToolType,
} from '../firestore/PromptToolType'
import { PromptPostAssignmentType } from '../models/PromptPostAssignmentType'
import { PostAssignmentPromptType } from '../firestore/PromptPostAssignmentType/types'
import {
  getPromptPostAssignmentType,
  updatePromptPostAssignmentType,
} from '../firestore/PromptPostAssignmentType'

export class AdminPromptCubit<Tab extends string> extends Cubit {
  repository: FirebaseRepository

  promptAndSubType:
    | { promptType: PromptType.rubric; subType: RubricType }
    | { promptType: PromptType.postRoomState; subType: PostRoomStatePromptType }
    | {
        promptType: PromptType.interactiveElement
        subType: InteractiveElementPromptType
      }
    | {
        promptType: PromptType.roomState
        subType: RoomStatePromptType
      }
    | { promptType: PromptType.tool; subType: ToolPromptType }
    | {
        promptType: PromptType.postAssignment
        subType: PostAssignmentPromptType
      }

  prompt:
    | PromptRubricType
    | PromptPostRoomStateType
    | PromptInteractiveElementType
    | PromptRoomStateType
    | PromptToolType
    | PromptPostAssignmentType

  versions: StaticModelCollection<
    | RubricPromptVersion
    | PostRoomStatePromptVersion
    | InteractiveElementPromptVersion
    | RoomStatePromptVersion
    | ToolPromptVersion
    | PostAssignmentPromptVersion
  >

  currentVersions: StaticModelCollection<CurrentPromptVersion>

  @observable tab: Tab

  constructor(
    repository: FirebaseRepository,
    {
      promptType,
      typeId,
      defaultTab,
    }: { promptType: PromptType; typeId: string; defaultTab: Tab }
  ) {
    super()
    makeObservable(this)
    this.repository = repository
    this.tab = defaultTab

    this.promptAndSubType = parsePromptAndSubtype(promptType, typeId)

    switch (promptType) {
      case PromptType.rubric: {
        this.prompt = PromptRubricType.empty(repository)
        this.versions = RubricPromptVersion.emptyCollection(repository)
        break
      }
      case PromptType.postRoomState: {
        this.prompt = PromptPostRoomStateType.empty(repository)
        this.versions = PostRoomStatePromptVersion.emptyCollection(repository)
        break
      }
      case PromptType.interactiveElement: {
        this.prompt = PromptInteractiveElementType.empty(repository)
        this.versions =
          InteractiveElementPromptVersion.emptyCollection(repository)
        break
      }
      case PromptType.roomState: {
        this.prompt = PromptRoomStateType.empty(repository)
        this.versions = RoomStatePromptVersion.emptyCollection(repository)
        break
      }
      case PromptType.tool: {
        this.prompt = PromptToolType.empty(repository)
        this.versions = ToolPromptVersion.emptyCollection(repository)
        break
      }
      case PromptType.postAssignment: {
        this.prompt = PromptPostAssignmentType.empty(repository)
        this.versions = PostAssignmentPromptVersion.emptyCollection(repository)
        break
      }
    }

    this.currentVersions = CurrentPromptVersion.emptyCollection(repository)
  }

  initialize(): void {
    switch (this.promptAndSubType.promptType) {
      case PromptType.rubric: {
        this.addStream(
          getPromptVersions(this.repository, this.promptAndSubType),
          (m) => this.versions.replaceModels(m)
        )
        this.addStream(
          getCurrentPromptVersions(this.repository, this.promptAndSubType),
          (m) => this.currentVersions.replaceModels(m)
        )
        this.addStream(
          getRoomPromptType(this.repository, {
            id: this.promptAndSubType.subType.toString(),
          }),
          (m) => {
            if (!(this.prompt instanceof PromptRubricType))
              throw new Error(`Prompt is not a PromptRubricType`)
            this.prompt.replaceModel(m)
          }
        )

        break
      }
      case PromptType.postRoomState: {
        this.addStream(
          getPromptVersions(this.repository, this.promptAndSubType),
          (m) => this.versions.replaceModels(m)
        )

        this.addStream(
          getCurrentPromptVersions(this.repository, this.promptAndSubType),
          (m) => {
            this.currentVersions.replaceModels(m)
          }
        )

        this.addStream(
          getPromptPostRoomStateType(this.repository, {
            type: this.promptAndSubType.subType,
          }),
          (m) => {
            if (!(this.prompt instanceof PromptPostRoomStateType))
              throw new Error(`Prompt is not PromptPostRoomStateType`)
            this.prompt.replaceModel(m)
          }
        )

        break
      }
      case PromptType.interactiveElement: {
        this.addStream(
          getPromptVersions(this.repository, this.promptAndSubType),
          (m) => this.versions.replaceModels(m)
        )

        this.addStream(
          getCurrentPromptVersions(this.repository, this.promptAndSubType),
          (m) => {
            this.currentVersions.replaceModels(m)
          }
        )

        this.addStream(
          getPromptInteractiveElementType(this.repository, {
            type: this.promptAndSubType.subType,
          }),
          (m) => {
            if (!(this.prompt instanceof PromptInteractiveElementType))
              throw new Error(`Prompt is not PromptInteractiveElementType`)
            this.prompt.replaceModel(m)
          }
        )

        break
      }
      case PromptType.roomState: {
        this.addStream(
          getPromptVersions(this.repository, this.promptAndSubType),
          (m) => this.versions.replaceModels(m)
        )

        this.addStream(
          getCurrentPromptVersions(this.repository, this.promptAndSubType),
          (m) => {
            this.currentVersions.replaceModels(m)
          }
        )

        this.addStream(
          getPromptRoomStateType(this.repository, {
            type: this.promptAndSubType.subType,
          }),
          (m) => {
            if (!(this.prompt instanceof PromptRoomStateType))
              throw new Error(`Prompt is not PromptRoomStateType`)
            this.prompt.replaceModel(m)
          }
        )

        break
      }
      case PromptType.tool: {
        this.addStream(
          getPromptVersions(this.repository, this.promptAndSubType),
          (m) => this.versions.replaceModels(m)
        )

        this.addStream(
          getCurrentPromptVersions(this.repository, this.promptAndSubType),
          (m) => {
            this.currentVersions.replaceModels(m)
          }
        )

        this.addStream(
          getPromptToolType(this.repository, {
            type: this.promptAndSubType.subType,
          }),
          (m) => {
            if (!(this.prompt instanceof PromptToolType))
              throw new Error(`Prompt is not PromptToolType`)
            this.prompt.replaceModel(m)
          }
        )

        break
      }
      case PromptType.postAssignment: {
        this.addStream(
          getPromptVersions(this.repository, this.promptAndSubType),
          (m) => this.versions.replaceModels(m)
        )

        this.addStream(
          getCurrentPromptVersions(this.repository, this.promptAndSubType),
          (m) => {
            this.currentVersions.replaceModels(m)
          }
        )

        this.addStream(
          getPromptPostAssignmentType(this.repository, {
            type: this.promptAndSubType.subType,
          }),
          (m) => {
            if (!(this.prompt instanceof PromptPostAssignmentType))
              throw new Error(`Prompt is not PromptPostAssignmentType`)
            this.prompt.replaceModel(m)
          }
        )

        break
      }
    }
  }

  updatePromptDetails = async (params: {
    title: string
    description: string
  }) => {
    const { promptType, subType } = this.promptAndSubType

    switch (promptType) {
      case PromptType.rubric:
        return await updatePromptRubricType(this.repository, {
          ...params,
          rubricType: subType,
        })
      case PromptType.postRoomState:
        return await updatePromptPostRoomStateType(this.repository, {
          ...params,
          postRoomStateType: subType,
        })
      case PromptType.interactiveElement:
        return await updatePromptInteractiveElementType(this.repository, {
          ...params,
          interactiveElementType: subType,
        })
      case PromptType.roomState:
        return await updatePromptRoomStateType(this.repository, {
          ...params,
          roomStateType: subType,
        })
      case PromptType.tool:
        return await updatePromptToolType(this.repository, {
          ...params,
          toolType: subType,
        })
      case PromptType.postAssignment: {
        return await updatePromptPostAssignmentType(this.repository, {
          ...params,
          postAssignmentType: subType,
        })
      }
    }
  }

  updateRubricPromptScoreDescription = async (
    args: Omit<
      Parameters<typeof updatePromptRubricTypeScoreDescription>[1],
      'rubricType'
    >
  ) => {
    const { promptType, subType } = this.promptAndSubType

    if (promptType !== PromptType.rubric)
      throw new Error('Prompt type is not rubric')

    return await updatePromptRubricTypeScoreDescription(this.repository, {
      ...args,
      rubricType: subType,
    })
  }

  @computed
  get currentVersion() {
    if (!this.currentVersions.isLoaded)
      return CurrentPromptVersion.empty(this.repository)

    if (!this.currentVersions.models.length)
      throw new Error('No current version found')

    return this.currentVersions.models[0]
  }

  @computed
  get ready(): boolean {
    return (
      this.prompt.isLoaded &&
      this.versions.isLoaded &&
      this.currentVersions.isLoaded
    )
  }

  @computed
  get promptType() {
    return this.promptAndSubType.promptType
  }

  @computed
  get subType() {
    return this.promptAndSubType.subType
  }

  @action
  changeTab(tab: typeof this.tab): void {
    this.tab = tab
  }
}

function parsePromptAndSubtype(
  promptType: PromptType,
  typeId: string
): AdminPromptCubit<'irrelevant'>['promptAndSubType'] {
  switch (promptType) {
    case PromptType.rubric: {
      if (!Object.values(RubricType).includes(Number(typeId) as RubricType))
        throw new Error(`Invalid RubricType: ${typeId}`)
      return { promptType, subType: Number(typeId) as RubricType }
    }
    case PromptType.postRoomState: {
      if (
        !Object.values(PostRoomStatePromptType).includes(
          typeId as PostRoomStatePromptType
        )
      )
        throw new Error(`Invalid PostRoomStatePromptType: ${typeId}`)
      return { promptType, subType: typeId as PostRoomStatePromptType }
    }
    case PromptType.interactiveElement: {
      if (
        !Object.values(InteractiveElementPromptType).includes(
          typeId as InteractiveElementPromptType
        )
      )
        throw new Error(`Invalid PostRoomStatePromptType: ${typeId}`)
      return { promptType, subType: typeId as InteractiveElementPromptType }
    }
    case PromptType.roomState: {
      if (
        !Object.values(RoomStatePromptType).includes(
          typeId as RoomStatePromptType
        )
      )
        throw new Error(`Invalid RoomStatePromptType: ${typeId}`)
      return { promptType, subType: typeId as RoomStatePromptType }
    }
    case PromptType.tool: {
      if (!Object.values(ToolPromptType).includes(typeId as ToolPromptType))
        throw new Error(`Invalid ToolPromptType: ${typeId}`)
      return { promptType, subType: typeId as ToolPromptType }
    }
    case PromptType.postAssignment: {
      if (
        !Object.values(PostAssignmentPromptType).includes(
          typeId as PostAssignmentPromptType
        )
      )
        throw new Error(`Invalid PostAssignmentPromptType: ${typeId}`)
      return { promptType, subType: typeId as PostAssignmentPromptType }
    }
  }
}
